import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { Label, OnlineOrder } from '@restaurant/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orderKeys } from './queryKeys';
import { trackEvent } from '@/lib/analytics/umami-client';
import {
  errorCodeOf,
  orderActionPayload,
  sinceLastDone,
  type OrderActionFacts,
  type OrderActionOutcome,
} from '@/features/orders/utils/order-analytics';

/**
 * Analytics context the call site hands to a mutation. It rides on the variables
 * because only the CALL SITE knows the order row, the pane and the sheet dwell,
 * while only the HOOK sees the 409 no-op and the throw - and both halves belong
 * in one event. `mutationFn` destructures the request fields explicitly, so
 * `track` never reaches the wire.
 */
type TrackFacts = OrderActionFacts;

function trackOrder(
  track: TrackFacts,
  outcome: OrderActionOutcome['outcome'],
  err?: unknown,
): void {
  trackEvent(
    'order_action',
    orderActionPayload(
      { ...track, outcome, ...(outcome === 'error' ? { errorCode: errorCodeOf(err) } : {}) },
      Date.now(),
    ),
  );
}

// Gap between completions, for telling real-time clearing from an end-of-shift
// sweep. Page-scoped: a reload restarts it, which costs one measurement and
// leaves the burst pattern intact.
let lastDoneMs: number | null = null;

/**
 * Accept a PENDING order. Resolves `null` when it was already accepted — the
 * owner's intent has happened, so that is a no-op to re-converge from. A
 * cancelled order still rejects: that one is news.
 *
 * The 409 is intercepted before `unwrap`, which is what turns >= 400 into a
 * throw; once it throws, the global dialog fires too. Same shape as the kitchen
 * board's `settleConditional`.
 */
export function useAcceptOrder() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      prepMinutesOverride,
      deliveryMinutesOverride,
    }: {
      orderId: string;
      prepMinutesOverride?: number;
      deliveryMinutesOverride?: number;
      track: TrackFacts;
    }): Promise<OnlineOrder | null> => {
      const result = await openapiClient.POST(
        '/api/restaurants/{restaurantId}/orders/{orderId}/accept',
        {
          params: { path: { restaurantId, orderId } },
          body: { prepMinutesOverride, deliveryMinutesOverride },
        },
      );
      if (isConflictWithCode(result, 'order.alreadyConfirmed')) return null;
      return unwrapResult(result);
    },
    onSuccess: (updated, { track }) => {
      // Nothing to seed on the no-op path — the invalidate below refetches the
      // real row, including the timing whoever won the race actually set.
      if (updated) queryClient.setQueryData(orderKeys.detail(restaurantId, updated.id), updated);
      trackOrder(track, updated ? 'ok' : 'already_taken');
    },
    onError: (err, { track }) => trackOrder(track, 'error', err),
    // onSettled, not onSuccess: the cancelled path throws, and that is exactly
    // where the list is provably stale — it still shows a tappable PENDING row.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    },
  });
}

export function isConflictWithCode(
  result: { error?: unknown; response: Response },
  code: string,
): boolean {
  const { error } = result;
  return (
    result.response.status === 409 &&
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === code
  );
}

export function useCancelOrder() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      orderId,
      reason,
      issueRefund,
    }: {
      orderId: string;
      reason?: string;
      // When the order's payment is refundable, whether cancelling also refunds
      // the customer. Omitted for non-refundable payments (backend ignores it).
      issueRefund?: boolean;
      track: TrackFacts;
    }): Promise<OnlineOrder | null> => {
      const result = await openapiClient.POST(
        '/api/restaurants/{restaurantId}/orders/{orderId}/cancel',
        {
          params: { path: { restaurantId, orderId } },
          body: {
            ...(reason ? { reason } : {}),
            ...(issueRefund !== undefined ? { issueRefund } : {}),
          },
        },
      );
      if (isConflictWithCode(result, 'order.alreadyCancelled')) return null;
      return unwrapResult(result);
    },
    onSuccess: (updated, { track }) => {
      if (updated) queryClient.setQueryData(orderKeys.detail(restaurantId, updated.id), updated);
      trackOrder(track, updated ? 'ok' : 'already_taken');
    },
    onError: (err, { track }) => trackOrder(track, 'error', err),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    },
  });
}

export function useCompleteOrder() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId }: { orderId: string; track: TrackFacts }) =>
      completeUnlessAlreadyCompleted(restaurantId, orderId),
    onSuccess: (updated, { track }) => {
      if (!updated) {
        trackOrder(track, 'already_taken');
        return;
      }
      queryClient.setQueryData(orderKeys.detail(restaurantId, updated.id), updated);
      const now = Date.now();
      trackOrder(
        track.action === 'done'
          ? { ...track, sinceLastDoneSeconds: sinceLastDone(lastDoneMs, now) }
          : track,
        'ok',
      );
      lastDoneMs = now;
    },
    onError: (err, { track }) => trackOrder(track, 'error', err),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
    },
  });
}

export async function completeUnlessAlreadyCompleted(
  restaurantId: string,
  orderId: string,
): Promise<OnlineOrder | null> {
  const path = { restaurantId, orderId };
  const result = await openapiClient.POST(
    '/api/restaurants/{restaurantId}/orders/{orderId}/complete',
    { params: { path } },
  );
  if (result.response.status === 409) {
    const current = await openapiClient
      .GET('/api/restaurants/{restaurantId}/orders/{orderId}', { params: { path } })
      .catch(() => null);
    if (current?.data?.status === 'COMPLETED') return null;
  }
  return unwrapResult(result);
}

/**
 * Replace an order's triage labels. Optimistic, because the chip row is a
 * tap-and-move-on interaction — a spinner per tap would defeat the point.
 *
 * `scope` serialises writes for one order, so rapid toggling applies in tap order
 * and the last request deterministically wins instead of racing.
 */
export function useSetOrderLabels(orderId: string) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  const detailKey = orderKeys.detail(restaurantId, orderId);

  return useMutation({
    scope: { id: `order-labels-${orderId}` },
    mutationFn: async (labels: Label[]) => {
      return unwrapResult(
        await openapiClient.PUT('/api/restaurants/{restaurantId}/orders/{orderId}/labels', {
          params: { path: { restaurantId, orderId } },
          body: { labelIds: labels.map((l) => l.id) },
        }),
      );
    },
    onMutate: async (labels) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<OnlineOrder>(detailKey);
      if (previous) queryClient.setQueryData<OnlineOrder>(detailKey, { ...previous, labels });
      return { previous };
    },
    onError: (_err, _labels, context) => {
      // Put the chips back where they were; the caller surfaces a toast.
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(orderKeys.detail(restaurantId, updated.id), updated);
      queryClient.invalidateQueries({ queryKey: orderKeys.all(restaurantId) });
      queryClient.invalidateQueries({ queryKey: ['purge', 'preview', restaurantId] });
    },
  });
}
